import { useInView } from '@/hooks/useInView';
import { useCountUp } from '@/hooks/useCountUp';

interface Stat {
  numeric: number;
  prefix?: string;
  suffix?: string;
  label: string;
  decimals?: number;
  duration?: number;
}

const stats: Stat[] = [
  { numeric: 15, suffix: '+', label: 'Years of Excellence', duration: 1400 },
  { numeric: 100, suffix: '%', label: 'Client Retention', duration: 1600 },
  { numeric: 24, suffix: '/7', label: 'Concierge Support', duration: 1000 },
  { numeric: 50, prefix: '$', suffix: 'M+', label: 'Portfolio Value Managed', duration: 1800 },
];

interface StatItemProps {
  stat: Stat;
  inView: boolean;
  delay: number;
}

function StatItem({ stat, inView, delay }: StatItemProps) {
  const count = useCountUp({
    target: stat.numeric,
    duration: stat.duration ?? 1600,
    start: inView,
    decimals: stat.decimals ?? 0,
  });

  const display = `${stat.prefix ?? ''}${count}${stat.suffix ?? ''}`;

  return (
    <div
      className="flex flex-col gap-2 transition-all duration-700 ease-out"
      style={{
        transitionDelay: inView ? `${delay}ms` : '0ms',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      <span className="text-4xl md:text-5xl font-semibold text-[#1A1A1A] tracking-tight tabular-nums">
        {display}
      </span>
      <span className="text-sm text-[#6B6B6B] font-light leading-snug">
        {stat.label}
      </span>
    </div>
  );
}

export default function AboutUs() {
  const [headerRef, headerInView] = useInView<HTMLDivElement>({ threshold: 0.15 });
  const [statsRef, statsInView] = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <section id="about" className="w-full bg-[#FAF9F7] py-28 px-6 md:px-16">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row gap-12 md:gap-24 mb-20 transition-all duration-700 ease-out ${
            headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="md:w-1/3">
            <span className="text-xs uppercase tracking-[0.25em] text-[#8B7355] font-semibold">
              — About Us
            </span>
          </div>
          <div className="md:w-2/3">
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A1A1A] leading-tight mb-8">
              A Higher Standard in Every Detail
            </h2>
            <div className="space-y-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed font-light">
              <p>
                Higher Standard Global was founded on a singular conviction: that exceptional property management demands more than routine oversight. It requires an unwavering commitment to excellence, discretion, and a deeply personal understanding of each client&apos;s vision.
              </p>
              <p>
                We operate exclusively by referral, serving a select group of property owners who expect nothing less than the highest standard of care for their investments. Our approach is bespoke — every property, every tenant relationship, and every service interaction is handled with the precision and attention it deserves.
              </p>
              <p>
                From long-term residential leases to short-term and vacation properties, we bring the same level of dedication and expertise to every engagement, ensuring your property performs at its full potential while you enjoy complete peace of mind.
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-[#E8E6E3]"
        >
          {stats.map((stat, index) => (
            <StatItem
              key={stat.label}
              stat={stat}
              inView={statsInView}
              delay={index * 100}
            />
          ))}
        </div>

      </div>
    </section>
  );
}